/**
 * Grid Cards Manager - Moon Moon Agency
 *
 * Pour chaque [data-grid-cards-slider] :
 *   - Init Splide (desktop : slider type slide; mobile : destroy:true via breakpoint -> grid CSS)
 *   - Cable les fleches custom (data-grid-cards-prev/next)
 *   - Met a jour la progress bar (data-grid-cards-progress)
 *   - Si data-needs-recommendations : fetch /recommendations/products?... + replace
 *
 * Cf. .claude/rules/javascript.md (Manager pattern).
 */

/*********************************************************************************************
 * 🃏 GRID CARDS MANAGER
 */
const GridCardsManager = {
	config: {
		selectors: {
			wrapper:   '[data-grid-cards-slider]',
			slider:    '.mm-grid-cards-grid.splide',
			prev:      '[data-grid-cards-prev]',
			next:      '[data-grid-cards-next]',
			progress:  '[data-grid-cards-progress]',
		},
	},

	init() {
		document.querySelectorAll(this.config.selectors.wrapper).forEach(w => this._initWrapper(w));
		window.log?.('info', '🃏 [GRID_CARDS]', 'Initialized');
	},

	async _initWrapper(wrapper) {
		// 1. Fetch recommendations si necessaire (avant init Splide)
		if (wrapper.hasAttribute('data-needs-recommendations')) {
			const swapped = await this._fetchRecommendations(wrapper);
			if (swapped) return; // _initWrapper sera ree-appele sur le nouveau wrapper
		}

		// 2. Init Splide
		const sliderEl = wrapper.querySelector(this.config.selectors.slider);
		if (!sliderEl || typeof Splide === 'undefined') {
			if (window.isDev) console.warn('🃏 [GRID_CARDS] sliderEl or Splide missing', { sliderEl, splideAvail: typeof Splide });
			return;
		}

		let config;
		try { config = JSON.parse(sliderEl.dataset.splide || '{}'); }
		catch (e) {
			console.error('🃏 [GRID_CARDS] data-splide JSON parse failed', e, sliderEl.dataset.splide);
			config = {};
		}

		if (window.isDev) console.log('🃏 [GRID_CARDS] Mounting section splide', { config, slidesCount: sliderEl.querySelectorAll('.splide__slide').length });

		const splide = new Splide(sliderEl, config).mount();
		wrapper._splide = splide;
		sliderEl._splide = splide; // marqueur anti-double-mount

		// 3. Custom arrows
		const prev = wrapper.querySelector(this.config.selectors.prev);
		const next = wrapper.querySelector(this.config.selectors.next);
		prev?.addEventListener('click', () => splide.go('<'));
		next?.addEventListener('click', () => splide.go('>'));

		const updateArrowsState = () => {
			try {
				const idx = splide.index;
				const len = splide.length;
				const perPage = splide.options.perPage || 1;
				if (prev) prev.toggleAttribute('disabled', idx <= 0);
				if (next) next.toggleAttribute('disabled', idx + perPage >= len);
			} catch (_) {
				// Splide destroyed (ex: breakpoint mobile) -> disable arrows
				prev?.setAttribute('disabled', '');
				next?.setAttribute('disabled', '');
			}
		};

		// 4. Progress bar
		const progressBar = wrapper.querySelector(this.config.selectors.progress);
		const updateProgress = () => {
			if (!progressBar) return;
			try {
				const end = splide.Components.Controller.getEnd() + 1;
				const rate = Math.min((splide.index + 1) / end, 1);
				progressBar.style.width = `${100 * rate}%`;
			} catch (_) {}
		};

		splide.on('mounted move moved resized', () => {
			updateArrowsState();
			updateProgress();
		});
		updateArrowsState();
		updateProgress();
	},

	async _fetchRecommendations(wrapper) {
		const productId = wrapper.dataset.productId;
		const sectionId = wrapper.dataset.sectionId;
		const intent = wrapper.dataset.intent || 'related';
		const limit = wrapper.dataset.limit || '8';
		if (!productId || !sectionId) return false;

		try {
			/* Prefixe avec window.Shopify.routes.root pour respecter la locale courante
			 * (= '/' sur la langue primaire FR, '/en/' sur EN, etc.). Sans ca, l'endpoint
			 * recommendations renvoie toujours du HTML en langue primaire (FR), meme sur /en/. */
			const root = window.Shopify?.routes?.root || '/';
			const url = `${root}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&intent=${encodeURIComponent(intent)}&limit=${encodeURIComponent(limit)}`;
			const res = await fetch(url);
			if (!res.ok) throw new Error(`status ${res.status}`);
			const html = await res.text();
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const newWrapper = doc.querySelector(this.config.selectors.wrapper);
			if (window.isDev) {
				const cards = doc.querySelectorAll(`${this.config.selectors.wrapper} [data-grid-card]`);
				console.log('🃏 [GRID_CARDS]', {
					url, cardsCount: cards.length,
					handles: Array.from(cards).map(c => c.dataset.productHandle),
				});
			}
			if (!newWrapper) {
				// Pas de recommendations dispo pour ce produit -> retire le placeholder + la section parent
				this._removeSection(wrapper);
				return true;
			}
			wrapper.replaceWith(newWrapper);
			// Re-scan les .splide injectes (sliders d'images per-card) - DOMContentLoaded a deja fired
			window.SplideManager?.init?.();
			this._initWrapper(newWrapper);
			return true;
		} catch (err) {
			window.log?.('warn', '🃏 [GRID_CARDS]', 'Recommendations fetch failed', { error: err.message });
			this._removeSection(wrapper);
			return false;
		}
	},

	/**
	 * Retire le wrapper ET la section Shopify parent (sinon il reste un wrapper vide avec background).
	 */
	_removeSection(wrapper) {
		const shopifySection = wrapper.closest('[id^="shopify-section-"]');
		(shopifySection || wrapper).remove();
	},
};

/*********************************************************************************************
 * 🚀 INIT
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => GridCardsManager.init());
} else {
	GridCardsManager.init();
}

window.GridCardsManager = GridCardsManager;
